import { useState, useEffect, useCallback } from 'react';
import { getRestaurantMenus } from '../api/menuService';
import { MenuSection } from '../types/menu';
import { getStatusMessages } from '../config/constants';
import { getCurrentLanguage } from '../config/languageConfig';

/**
 * 레스토랑 메뉴 목록을 가져오는 커스텀 훅
 * @param restaurantId 레스토랑 ID
 */
export const useRestaurantMenus = (restaurantId: string | number) => {
  const [menuSections, setMenuSections] = useState<MenuSection[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  const fetchMenus = useCallback(async () => {
    if (!restaurantId) {
      setError('Restaurant ID is required');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // 현재 언어 가져오기
      const currentLang = getCurrentLanguage();
      
      // 현재 언어를 API 호출에 전달
      const data = await getRestaurantMenus(restaurantId, currentLang);
      
      if (Array.isArray(data) && data.length === 0) {
        console.warn(`No menu items found for restaurant ${restaurantId}`);
      }
      
      setMenuSections(data);
    } catch (err) {
      console.error('Error fetching restaurant menus:', err);
      const STATUS_MESSAGES = getStatusMessages();
      setError(STATUS_MESSAGES.error.menus || 'Failed to load menus. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);
  
  // 언어 변경 이벤트 감지 및 메뉴 데이터 다시 로드
  useEffect(() => {
    const handleLanguageChange = () => {
      fetchMenus();
    };
    
    // 언어 변경 이벤트 리스너 등록
    window.addEventListener('language-changed', handleLanguageChange);
    
    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      window.removeEventListener('language-changed', handleLanguageChange);
    };
  }, [fetchMenus]);
  
  useEffect(() => {
    fetchMenus();
  }, [fetchMenus]);
  
  return { menuSections, loading, error, refetch: fetchMenus };
}; 